import { createIrisApiClient, basePaths } from 'API/clients/iris/irisapi'
import { filterParamSerializer } from 'API/utilities/filterParamSerializer'
import { ImageAnnotationType } from 'Modules/types/pages/imageReview/imageAnnotationType'
import type { AxiosResponse } from 'axios'

const irisApiClient = createIrisApiClient(basePaths.iris)
const baseAnnotationPath = 'annotation'
const baseAnnotationsPath = `annotations`

interface AnnotationDetails {
  annotations: Array<ImageAnnotationType>
  imageUuid: string
}

interface GroupPatchDetails extends AnnotationDetails {
  groupId: number
}

interface PatchImageAnnotationsResponse {
  status: 'success' | 'error'
  imageAnnotation: string
  error?: string
}

interface PostImageAnnotationGroupResponse {
  group_id: string
  annotations: string[]
  errors: { invalid_ids: string[] }
}

interface PatchImageAnnotationGroupResponse {
  groupId?: number
  annotations?: Array<number>
  error?: string
}

const postImageAnnotation = (annotationDetailsObj) => {
  const { imageUuid, annotationValues } = annotationDetailsObj
  const { annotationStatus, annotationType, defectBounds, defectType, defectStatus, defectComment } = annotationValues
  return irisApiClient.post(`/${baseAnnotationPath}/`, {
    annotation_status: annotationStatus,
    annotation_type: annotationType,
    defect_bounds: defectBounds,
    defect_type: defectType,
    defect_status: defectStatus,
    defect_comment: defectComment,
    image_uuid: imageUuid,
  })
}

const deleteImageAnnotation = (annotationUuid: string) =>
  irisApiClient.delete(`/${baseAnnotationPath}/${annotationUuid}/`)

const postBatchDeleteImageAnnotation = (annotationUuids: Array<string>) => {
  return irisApiClient.post(`/${baseAnnotationPath}/batch_delete/`, {
    annotation_uuids: annotationUuids,
  })
}

const patchImageAnnotation = (updateAnnotationDetailsObj: {
  annotationValues: Array<ImageAnnotationType>
  imageUuid: string
}): Promise<AxiosResponse<PatchImageAnnotationsResponse[]>> => {
  const { annotationValues } = updateAnnotationDetailsObj
  const annotationsData = annotationValues.map((annotation) => {
    const { annotationUuid, annotationStatus, annotationType, defectBounds, defectType, defectStatus, defectComment } =
      annotation
    return {
      annotation_uuid: annotationUuid,
      annotation_status: annotationStatus,
      annotation_type: annotationType,
      defect_bounds: defectBounds,
      defect_type: defectType,
      defect_status: defectStatus,
      defect_comment: defectComment,
    }
  })

  return irisApiClient.patch<PatchImageAnnotationsResponse[]>(`/${baseAnnotationPath}/`, {
    annotations: annotationsData,
  })
}

const postImageAnnotationGroup = (
  createGroupDetailsObj: AnnotationDetails
): Promise<AxiosResponse<PostImageAnnotationGroupResponse>> => {
  const { annotations } = createGroupDetailsObj
  const annotationIds = mapAnnotationsToIds(annotations)
  return irisApiClient.post<PostImageAnnotationGroupResponse>(`/${baseAnnotationsPath}/group/`, annotationIds)
}

const patchImageAnnotationGroup = (patchDetailsObj: {
  annotations: Array<ImageAnnotationType>
  groupId: number
  imageUuid: string
}): Promise<AxiosResponse<PatchImageAnnotationGroupResponse>> => {
  const { annotations, groupId } = patchDetailsObj
  const annotationIds = mapAnnotationsToIds(annotations)
  return irisApiClient.patch<PatchImageAnnotationGroupResponse>(
    `/${baseAnnotationsPath}/group/${groupId}/`,
    annotationIds
  )
}

const putImageAnnotationGroup = (putDetailsObjs: {
  annotationValues: Array<ImageAnnotationType>
  groupId: number
  imageUuid: string
}) => {
  const { annotationValues, groupId } = putDetailsObjs
  const annotationData = annotationValues.map((annotation) => {
    const { defectType, defectStatus, defectComment } = annotation
    return {
      defect_type: defectType,
      defect_status: defectStatus,
      defect_comment: defectComment,
    }
  })
  return irisApiClient.patch(`${baseAnnotationsPath}/group/${groupId}/`, annotationData)
}

const deleteImageAnnotationGroup = (deleteDetailsObj: { groupId: number; imageUuid: string }) => {
  const { groupId } = deleteDetailsObj
  return irisApiClient.delete(`${baseAnnotationsPath}/group/${groupId}/`)
}

const patchAnnotationsAndPostGroup = async (detailsObj: AnnotationDetails) => {
  try {
    const patchAnnotationsResult = await patchImageAnnotation({
      annotationValues: detailsObj.annotations,
      imageUuid: detailsObj.imageUuid,
    })

    const errors = patchAnnotationsResult.data
      .filter((result) => result.status === 'error')
      .map((result) => result.error)

    if (errors.length > 0) {
      throw Object.assign(new Error('Annotation Patch Errors'), { errors })
    }

    const postImageAnnotationGroupResult = await postImageAnnotationGroup({
      annotations: detailsObj.annotations,
      imageUuid: detailsObj.imageUuid,
    })

    if (postImageAnnotationGroupResult.errors.invalid_ids.length > 0) {
      throw Object.assign(new Error('Invalid Annotation IDs'), {
        invalid_ids: postImageAnnotationGroupResult.errors.invalid_ids,
      })
    }

    return postImageAnnotationGroupResult
  } catch (error) {
    console.error('Error in patchAnnotationsAndPostGroup:', error)
    throw error
  }
}

const patchAnnotationsAndPatchGroup = async (detailsObj: GroupPatchDetails) => {
  try {
    const patchAnnotationsResult = await patchImageAnnotation({
      annotationValues: detailsObj.annotations,
      imageUuid: detailsObj.imageUuid,
    })

    const errors = patchAnnotationsResult.data
      .filter((result) => result.status === 'error')
      .map((result) => result.error)

    if (errors.length > 0) {
      throw Object.assign(new Error('Annotation Patch Errors'), { errors })
    }

    const patchImageAnnotationGroupResult = await patchImageAnnotationGroup({
      annotations: detailsObj.annotations,
      groupId: detailsObj.groupId,
      imageUuid: detailsObj.imageUuid,
    })

    if (patchImageAnnotationGroupResult.error) {
      throw new Error(patchImageAnnotationGroupResult.error)
    }

    return patchImageAnnotationGroupResult
  } catch (error) {
    console.error('Error Patching Annotations and Patching Group:', error)
    throw error
  }
}

const getAnnotationsFiltered = (params) => {
  return irisApiClient.get(`/${baseAnnotationsPath}/`, {
    params,
    paramsSerializer: filterParamSerializer,
  })
}

function mapAnnotationsToIds(annotations: Array<ImageAnnotationType>) {
  return annotations.map((annotation) => annotation.annotationId)
}

export {
  postImageAnnotation,
  postBatchDeleteImageAnnotation,
  deleteImageAnnotation,
  patchImageAnnotation,
  postImageAnnotationGroup,
  patchImageAnnotationGroup,
  putImageAnnotationGroup,
  deleteImageAnnotationGroup,
  getAnnotationsFiltered,
  patchAnnotationsAndPostGroup,
  patchAnnotationsAndPatchGroup,
}
